import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
  type HubConnection,
} from '@microsoft/signalr';

import { getKeycloak } from '@/auth/keycloak';
import { getConfig } from '@/lib/config';

/**
 * Wire payload pushed by the backend's SignalRBulkJobNotifier — keep in sync
 * with Datahub.Application.Jobs.BulkJobNotification (camelCase over the wire).
 */
export interface BulkJobNotification {
  jobId: string;
  operation: string;
  status: string;
  total: number;
  processed: number;
  affected: number;
  skipped: number;
  error?: string | null;
  /** 'bulk' (default) or 'import' — both kinds share the bulkJobProgress/Completed events. */
  jobType?: 'bulk' | 'import';
  /** Import-only breakdown. */
  created?: number;
  updated?: number;
  failed?: number;
}

export interface JobsHubHandlers {
  onProgress: (n: BulkJobNotification) => void;
  onCompleted: (n: BulkJobNotification) => void;
  /** Fired on connect/disconnect/reconnect so callers can toggle the polling fallback. */
  onStateChange: (connected: boolean) => void;
}

const INITIAL_RETRY_MS = 10_000;

let connection: HubConnection | null = null;
let connectedTenantId: string | null = null;
let retryTimer: number | null = null;

export function isJobsHubConnected(): boolean {
  return connection?.state === HubConnectionState.Connected;
}

/**
 * Starts (or re-targets) the singleton connection to the backend's /hubs/jobs.
 * The hub lives behind the same /api proxy as the REST API; the JWT travels as
 * ?access_token= (WebSockets can't send an Authorization header) and the tenant
 * as ?tenantId=, validated server-side in JobsHub.OnConnectedAsync.
 */
export async function startJobsHub(tenantId: string, handlers: JobsHubHandlers): Promise<void> {
  if (connection && connectedTenantId === tenantId) return;
  await stopJobsHub();

  const url = `${getConfig().apiUrl}/hubs/jobs?tenantId=${encodeURIComponent(tenantId)}`;
  const conn = new HubConnectionBuilder()
    .withUrl(url, {
      accessTokenFactory: async () => {
        const keycloak = getKeycloak();
        try {
          await keycloak.updateToken(30);
        } catch {
          // Fall through with the current token; a 401 surfaces as a failed
          // (re)connect and the polling fallback takes over.
        }
        return keycloak.token ?? '';
      },
    })
    .withAutomaticReconnect()
    .configureLogging(LogLevel.Warning)
    .build();

  conn.on('bulkJobProgress', handlers.onProgress);
  conn.on('bulkJobCompleted', handlers.onCompleted);
  conn.onreconnecting(() => handlers.onStateChange(false));
  conn.onreconnected(() => handlers.onStateChange(true));
  conn.onclose(() => handlers.onStateChange(false));

  connection = conn;
  connectedTenantId = tenantId;

  // withAutomaticReconnect only covers drops AFTER a successful connect — a
  // failed initial start needs its own retry loop.
  const tryStart = async () => {
    if (connection !== conn) return; // superseded by a tenant switch / stop
    try {
      await conn.start();
      handlers.onStateChange(true);
    } catch {
      handlers.onStateChange(false);
      retryTimer = window.setTimeout(() => void tryStart(), INITIAL_RETRY_MS);
    }
  };
  await tryStart();
}

export async function stopJobsHub(): Promise<void> {
  if (retryTimer !== null) {
    window.clearTimeout(retryTimer);
    retryTimer = null;
  }
  const conn = connection;
  connection = null;
  connectedTenantId = null;
  if (conn) {
    try {
      await conn.stop();
    } catch {
      // Already stopped/never started — nothing to clean up.
    }
  }
}
